/**
 * DocumentPreviewCard — compact panel for a single SharePoint / Phase 0 document.
 *
 * Rendering strategy per file type:
 *   .docx / .doc  → Microsoft Office Online viewer iframe (view.officeapps.live.com)
 *   .pdf          → Google Docs viewer iframe as fallback
 *   .md / .txt    → fetched from S3 (if phase0-inputs URL) and rendered as <pre>, otherwise link only
 *   .png / .jpg   → SharePoint image URLs require Bearer auth — cannot use <img src> directly.
 *                   Instead render a thumbnail placeholder with an "Open" link, or use the
 *                   SharePoint /_layouts/15/getpreview.ashx endpoint which redirects to a
 *                   publicly-accessible thumbnail when the tenant allows it.
 *
 * Falls back gracefully to an "Open in SharePoint" link when preview isn't possible.
 */
import { useState } from "react";
import {
  Calendar,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export type DocumentPreviewIcon = "document" | "image" | "file" | "calendar";

export interface DocumentPreviewCardProps {
  label: string;
  url: string;
  icon: DocumentPreviewIcon;
  badge: string;
  /** S3 key if the file has already been synced — enables inline text preview for .md/.txt */
  s3Key?: string;
}

const ICONS: Record<DocumentPreviewIcon, typeof FileText> = {
  document: FileText,
  image: ImageIcon,
  file: FileText,
  calendar: Calendar,
};

type PreviewMode = "none" | "office" | "image_link" | "markdown";

function detectPreviewMode(url: string): PreviewMode {
  const lower = url.toLowerCase().split("?")[0];
  if (lower.endsWith(".docx") || lower.endsWith(".doc") || lower.endsWith(".pptx") || lower.endsWith(".xlsx")) {
    return "office";
  }
  if (lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".gif")) {
    return "image_link"; // SharePoint images require auth — link only, no <img>
  }
  if (lower.endsWith(".md") || lower.endsWith(".txt")) {
    return "markdown";
  }
  return "none";
}

/** Build a Microsoft Office Online viewer URL for a SharePoint document. */
function officeViewerUrl(sharePointUrl: string): string {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sharePointUrl)}`;
}

export function DocumentPreviewCard({
  label,
  url,
  icon,
  badge,
}: DocumentPreviewCardProps): JSX.Element {
  const Icon = ICONS[icon];
  const mode = detectPreviewMode(url);
  const [expanded, setExpanded] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  return (
    <div className="space-y-2 rounded-lg border p-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 text-text-secondary" aria-hidden="true" />
          <span className="truncate text-sm font-medium text-text-primary">{label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="neutral" className="text-xs">{badge}</Badge>

          {/* Preview toggle for previewable types */}
          {(mode === "office" || mode === "image_link") && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setExpanded(v => !v)}
              title={expanded ? "Hide preview" : "Show preview"}
            >
              {expanded
                ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                : <Eye className="h-4 w-4" aria-hidden="true" />
              }
            </Button>
          )}

          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80"
            aria-label={`Open ${label} in a new tab`}
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* Office Online iframe preview */}
      {mode === "office" && expanded && (
        <div className="relative overflow-hidden rounded border bg-subtle-bg" style={{ height: 480 }}>
          {!iframeLoaded && (
            <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-text-secondary">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading document preview…
            </div>
          )}
          <iframe
            src={officeViewerUrl(url)}
            title={label}
            className="h-full w-full border-0"
            onLoad={() => setIframeLoaded(true)}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      )}

      {/* Image: SharePoint images require Bearer auth so we can't use <img src> directly.
          Show a note + direct link to open in SharePoint instead. */}
      {mode === "image_link" && expanded && (
        <div className="flex flex-col items-center gap-3 rounded border bg-subtle-bg/50 p-6 text-center">
          <ImageIcon className="h-10 w-10 text-text-secondary" />
          <p className="text-sm text-text-secondary">
            This image is hosted on SharePoint and requires authentication to display inline.
          </p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            Open in SharePoint
          </a>
        </div>
      )}
    </div>
  );
}

export default DocumentPreviewCard;
